import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import toastOptions from '../../utils/toastOptions';
import { TransactionType } from '../../models/TransactionType';
import { currencyMask, displayDate } from '../../utils/masks';
import moment from '../../utils/momentConfig';

import '../shared/card.css';
import 'react-toastify/dist/ReactToastify.css';
import { faAnglesRight, faCircleCheck, faCircleMinus, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FinancialCategory } from '../../models/enums/FinancialCategory';

interface TransactionItem {
  id: number;
  description?: string;
  amount: number;
  dueDate?: Date;
  type: TransactionType;
  category: FinancialCategory;
  paidAt?: Date;
}

export default function DashboardFinancial() {
  const [monthlyIncome, setMonthlyIncome] = useState<number>(0);
  const [monthlyExpense, setMonthlyExpense] = useState<number>(0);
  const [recentTransactions, setRecentTransactions] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
        const [transactionsMonthResponse, transactionsRecentResponse] = await Promise.all([
          api.get('/transactions', {
            params: {
              page: 0,
              limit: 0,
              startDate: startOfMonth,
              endDate: endOfMonth
            }
          }),
          api.get('/transactions', {
            params: {
              page: 1,
              limit: 5,
              startDate: startOfMonth,
              endDate: endOfMonth
            }
          })
        ]);

        const transactions = transactionsMonthResponse?.data?.data ?? [];
        const income = transactions
          .filter((transaction: any) => transaction.type === TransactionType.INCOME)
          .reduce((total: number, transaction: any) => total + Number(transaction.amount || 0), 0);
        const expense = transactions
          .filter((transaction: any) => transaction.type === TransactionType.EXPENSE)
          .reduce((total: number, transaction: any) => total + Number(transaction.amount || 0), 0);

        setMonthlyIncome(income);
        setMonthlyExpense(expense);
        setRecentTransactions(transactionsRecentResponse?.data?.data ?? []);
      } catch (e: any) {
        e.messages?.forEach((m: string) => {
          toast.error(m, toastOptions);
        });
        setMonthlyIncome(0);
        setMonthlyExpense(0);
        setRecentTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const monthlyBalance = monthlyIncome - monthlyExpense;

  return (
    <div id="page-dashboard-financial" className="uk-section uk-section-muted uk-padding-small">
      <div className="uk-grid-small" uk-grid="true">
        <div className="uk-width-1-1@m">
          <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
            <div className="uk-card-header uk-flex uk-flex-middle uk-flex-between">
              <h3 className="uk-card-title uk-margin-remove-bottom">Resumo Financeiro <b>{moment().format('MMMM/YYYY').toUpperCase()}</b></h3>
              <div className="uk-flex uk-flex-middle uk-grid-small" uk-grid="true">
                <Link
                  className="uk-button uk-button-success uk-button-small uk-margin-xsmall-right"
                  to={`/financeiro/transacoes/novo?type=${TransactionType.INCOME}`}
                >
                  Receita <FontAwesomeIcon icon={faCirclePlus} />
                </Link>
                <Link
                  className="uk-button uk-button-danger uk-button-small"
                  to={`/financeiro/transacoes/novo?type=${TransactionType.EXPENSE}`}
                >
                  Despesa <FontAwesomeIcon icon={faCircleMinus} />
                </Link>
              </div>
            </div>

            <div className="uk-card-body uk-position-relative">
              {loading && (
                <div
                  className="uk-position-cover uk-overlay-primary uk-flex uk-flex-center uk-flex-middle"
                  style={{ zIndex: 20 }}
                >
                  <div className="uk-spinner" style={{ width: 48, height: 48 }} />
                </div>
              )}

              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1-3@m">
                  <div className="uk-card uk-card-success uk-card-small uk-card-body uk-text-center">
                    <span>Total de Receitas</span>
                    <div className="uk-text-large uk-text-bold">
                      {currencyMask(monthlyIncome)}
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-3@m">
                  <div className="uk-card uk-card-danger uk-card-small uk-card-body uk-text-center">
                    <span>Total de Despesas</span>
                    <div className="uk-text-large uk-text-bold">
                      {currencyMask(monthlyExpense)}
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-3@m">
                  <div className="uk-card uk-card-default uk-card-small uk-card-body uk-text-center">
                    <div className="uk-text-meta">Saldo Atual</div>
                    <div className={`uk-text-large uk-text-bold ${monthlyBalance >= 0 ? 'uk-text-success' : 'uk-text-danger'}`}>
                      {currencyMask(monthlyBalance)}
                    </div>
                  </div>
                </div>
              </div>

              <hr className="uk-margin" />

              <div className="uk-flex uk-flex-middle uk-flex-between uk-margin-small-bottom">
                <h4 className="uk-margin-remove">Últimas transações</h4>
                <Link className="uk-button uk-button-primary uk-button-small" to="/financeiro/transacoes">
                  Ver todas <FontAwesomeIcon icon={faAnglesRight} />
                </Link>
              </div>

              {recentTransactions.length === 0 ? (
                <p className="uk-text-meta uk-margin-remove">Nenhuma transação encontrada.</p>
              ) : (
                <ul className="uk-list uk-list-divider uk-margin-remove-top">
                  {recentTransactions.map((transaction) => (
                    <li key={transaction.id}>
                      <div className="uk-flex uk-flex-middle uk-flex-between">
                        <div className="uk-flex uk-flex-column">
                          <span className="uk-text-small uk-text-bold uk-text-nowrap">
                            {transaction.category || 'Sem descrição'}
                            {transaction.paidAt && <FontAwesomeIcon icon={faCircleCheck} className='uk-margin-xsmall-left uk-text-success' />}
                          </span>
                          <span className="uk-text-meta uk-text-xsmall">
                            {displayDate(transaction.dueDate?.toString() || '')}
                          </span>
                        </div>
                        <span className={`uk-text-bold ${transaction.type === TransactionType.INCOME ? 'uk-text-success' : 'uk-text-danger'}`}>
                          {transaction.type === TransactionType.INCOME ? '+' : '-'} {currencyMask(transaction.amount || 0)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
